import { BaseOrganizationEndpoints } from './base';
import { PaginationOptions } from '../entities/pagination_options';
import {
  CalendarCreateSyncJobRequest,
  CalendarIntegrationSyncRequest,
  CalendarScheduleSyncRequest,
  CalendarCreateOrUpdateRequest,
  CalendarListenerCreateOrUpdateRequest,
  CalendarListenerListeningResponse,
  CalendarMarkListenerAsStartedRequest,
  CalendarSyncRequest,
  CalendarLeaveSyncRequest,
  ICalendar,
  ICalendarAuthorization,
  ICalendarAuthorizationForSyncing,
  CalendarAuthorizationCreateOrUpdateRequest,
  CalendarAuthorizationIntentCreateOrUpdateRequest,
  ICalendarAuthorizationIntent,
  ICalendarAuthorizationIntentForSyncing,
  ICalendarEvent,
  ICalendarListener,
  ICalendarPendingEvent,
  ICalendarSyncCheck,
  ICalendarSyncJob,
  IPendingCalendarEvent,
  IPendingCalendarSyncJob,
  IPendingEventSyncRequest,
  ResGetCalendars,
  ResGetCalendarSyncJobs,
  CalendarType,
  CalendarSource,
  ResHasAuthorization
} from '../entities/calendar';
import { CoreIntegrationTypes } from '../types/core-integration-types';

export class Calendar extends BaseOrganizationEndpoints {
  async getOrganizationCalendar(
    authorization: string,
    organizationId: string,
    id: string
  ) {
    return this.requestWithStatus<ICalendar>(
      'GET',
      `/organizations/${organizationId}/calendars/${id}`,
      null,
      {
        authHeader: `Basic ${authorization}`
      }
    );
  }

  async getCalendar(id: string) {
    return this.requestWithStatus<ICalendar>('GET', `/calendars/${id}`);
  }

  getAuthorizationUrl(source: CalendarSource, organizationId: string): string {
    return `https://id.gettactic.com/enterprise/calendar/setup/${source}/${organizationId}`;
  }

  async hasAuthorization(source: CalendarSource): Promise<boolean> {
    const response = await this.requestWithStatus<ResHasAuthorization>(
      'GET',
      `/calendars/authorizations/has_valid?calendarSource=${source}`
    );
    return response.ok && response.result!.has_authorization;
  }

  async getCalendarByResourceId(resourceId: string) {
    return this.requestWithStatus<ICalendar>(
      'GET',
      `/calendars/reference/${resourceId}`
    );
  }

  async fetchCalendarPending(authorization: string) {
    return this.requestWithStatus<ICalendarPendingEvent>(
      'PUT',
      `/calendars/pending`,
      null,
      {
        authHeader: `Basic ${authorization}`
      }
    );
  }

  async markCalendarProcessed(authorization: string, jobId: string) {
    return this.requestWithStatus<{ job_id: string }>(
      'DELETE',
      `/calendars/pending/${jobId}`,
      null,
      {
        authHeader: `Basic ${authorization}`
      }
    );
  }

  async all(
    sources: CalendarSource[] | null,
    types: CalendarType[] | null,
    integrationIds: string[] | null,
    includeAllResources: boolean | null,
    pagination: PaginationOptions = {
      cursor: null,
      limit: null
    }
  ) {
    let url = '/calendars';
    const params = new URLSearchParams();
    if (sources !== null) {
      sources.forEach((e) => {
        params.append('calendarSource', e);
      });
    }
    if (types !== null) {
      types.forEach((e) => {
        params.append('calendarType', e);
      });
    }
    if (integrationIds !== null) {
      integrationIds.forEach((e) => {
        params.append('integrationId', e);
      });
    }
    if (includeAllResources) {
      params.append('include-all-resources', String(includeAllResources));
    }
    if (params.size > 0) {
      url += `?${params.toString()}`;
    }

    return this.requestWithStatus<ResGetCalendars>(
      'GET',
      this.paginatedEndpointUrl(url, pagination)
    );
  }

  async allSyncJobs(
    calendarId: string,
    pagination: PaginationOptions = {
      cursor: null,
      limit: null
    }
  ) {
    const url = `/calendars/${calendarId}/sync_jobs`;
    return this.requestWithStatus<ResGetCalendarSyncJobs>(
      'GET',
      this.paginatedEndpointUrl(url, pagination)
    );
  }

  async allForSync(
    authorization: string,
    organizationId: string,
    integrationIds: string[],
    sources: CalendarSource[] | null = null,
    types: CalendarType[] | null = null
  ) {
    if (integrationIds.length === 0) {
      throw new Error('Missing integration IDs');
    }

    let url = `/calendars/${organizationId}/for_sync`;
    const params = new URLSearchParams();
    integrationIds.forEach((e) => {
      params.append('integrationId', e);
    });
    if (sources !== null) {
      sources.forEach((e) => {
        params.append('calendarSource', e);
      });
    }
    if (types !== null) {
      types.forEach((e) => {
        params.append('calendarType', e);
      });
    }
    url += `?${params.toString()}`;

    return this.requestWithStatus<ResGetCalendars>(
      'GET',
      this.paginatedEndpointUrl(url, {
        cursor: null,
        limit: `${integrationIds.length}`
      }),
      null,
      { authHeader: `Basic ${authorization}` }
    );
  }

  async createOrUpdateCalendar(
    id: string | null,
    body: CalendarCreateOrUpdateRequest,
    slug: string | null = null
  ) {
    const basePath = '/v1/calendars';

    return this.requestWithStatus<ICalendar>(
      id !== null ? 'PUT' : 'POST',
      id !== null ? `${basePath}/${id}` : basePath,
      body
    );
  }

  async deleteCalendar(id: string) {
    return this.requestWithStatus<void>('DELETE', `/v1/calendars/${id}`);
  }

  async deauthorizeIntegration(integrationType: CoreIntegrationTypes) {
    if (integrationType !== CoreIntegrationTypes.microsoft && integrationType !== CoreIntegrationTypes.google) {
      throw new Error('Invalid integration type');
    }

    return this.requestWithStatus<ICalendarSyncJob>(
      'DELETE',
      `/v1/calendars/authorization/${integrationType}`
    );
  }

  /**
   * @deprecated  Use createOrUpdateCalendar(body: CalendarCreateOrUpdateRequest)
   */
  createOrUpdate(
    organizationId: string,
    organizationSlug: string,
    source: CalendarSource,
    type: CalendarType,
    referenceId: string,
    integrationId: string,
    isEnabled: boolean,
    isPullEnabled: boolean,
    isPushEnabled: boolean,
    id: string | null = null,
  ) {
    return this.createOrUpdateCalendar(id, {
      organization_id: organizationId,
      source,
      type,
      reference_id: referenceId,
      integration_id: integrationId,
      is_enabled: isEnabled,
      is_pull_enabled: isPullEnabled,
      is_push_enabled: isPushEnabled,
      sync_events: true,
      sync_leaves: false
    }, organizationSlug);
  }

  async sync(
    authorization: string,
    organizationId: string,
    calendarId: string | null,
    body: CalendarSyncRequest
  ) {
    return this.requestWithStatus<ICalendar>(
      'PUT',
      calendarId !== null
        ? `/organizations/${organizationId}/calendars/${calendarId}/sync`
        : `/organizations/${organizationId}/calendars/sync`,
      body,
      { authHeader: `Basic ${authorization}` }
    );
  }

  async syncEventRequest(
    authorization: string,
    calendarId: string,
    body: CalendarScheduleSyncRequest
  ) {
    return this.requestWithStatus<ICalendar>(
      'PUT',
      `/calendars/${calendarId}/events/requests/sync`,
      body,
      {
        authHeader: `Basic ${authorization}`
      }
    );
  }

  async syncEventRequestGetForSyncing(
    authorization: string,
    organizationId: string | null = null
  ) {
    const url =
      organizationId !== null
        ? `/calendars/events/requests/for_syncing?organization=${encodeURIComponent(organizationId)}`
        : `/calendars/events/requests/for_syncing`;
    return this.requestWithStatus<IPendingEventSyncRequest>('PUT', url, null, {
      authHeader: `Basic ${authorization}`
    });
  }

  async syncEvent(
    authorization: string,
    calendarId: string,
    body: CalendarScheduleSyncRequest
  ) {
    return this.requestWithStatus<ICalendar>(
      'PUT',
      `/calendars/${calendarId}/events/sync`,
      body,
      {
        authHeader: `Basic ${authorization}`
      }
    );
  }

  async syncEventCreation(
    authorization: string,
    calendarId: string,
    scheduleId: string,
    body: CalendarIntegrationSyncRequest
  ) {
    return this.requestWithStatus<ICalendar>(
      'POST',
      `/calendars/${calendarId}/events/sync/schedules/${scheduleId}`,
      body,
      {
        authHeader: `Basic ${authorization}`
      }
    );
  }

  async syncEventDeletion(
    authorization: string,
    calendarId: string,
    eventId: string
  ) {
    return this.requestWithStatus<ICalendar>(
      'DELETE',
      `/calendars/${calendarId}/events/sync`,
      {
        event_id: eventId
      },
      {
        authHeader: `Basic ${authorization}`
      }
    );
  }

  async transform(
    authorization: string,
    organizationId: string,
    scheduleId: string
  ) {
    return this.requestWithStatus<ICalendarEvent>(
      'GET',
      `/calendars/${organizationId}/schedules/${scheduleId}/transform`,
      null,
      {
        authHeader: `Basic ${authorization}`
      }
    );
  }

  async fetchPending(authorization: string) {
    return this.requestWithStatus<IPendingCalendarEvent>(
      'PUT',
      `/calendars/schedules/updated/pending`,
      null,
      {
        authHeader: `Basic ${authorization}`
      }
    );
  }

  async markProcessed(
    authorization: string,
    organizationId: string,
    jobId: string
  ) {
    return this.requestWithStatus<{ job_id: string }>(
      'DELETE',
      `/calendars/${organizationId}/schedules/updated/${jobId}`,
      null,
      {
        authHeader: `Basic ${authorization}`
      }
    );
  }

  async syncLeave(
    authorization: string,
    calendarId: string,
    body: CalendarLeaveSyncRequest
  ) {
    return this.requestWithStatus<ICalendar>(
      'PUT',
      `/calendars/${calendarId}/leave/sync`,
      body,
      {
        authHeader: `Basic ${authorization}`
      }
    );
  }

  async createOrUpdateSyncJob(
    authorization: string,
    organizationId: string,
    calendarId: string,
    body: CalendarCreateSyncJobRequest
  ) {
    return this.requestWithStatus<ICalendarSyncJob>(
      'PUT',
      `/organizations/${organizationId}/calendars/${calendarId}/sync_jobs`,
      body,
      {
        authHeader: `Basic ${authorization}`
      }
    );
  }

  async getSyncJobForSyncing(
    authorization: string,
    organizationId: string | null = null,
    calendarId: string | null = null,
    jobId: string | null = null,
    sources: CalendarSource[] | null = null,
    types: CalendarType[] | null = null
  ) {
    let url =
      organizationId !== null && calendarId !== null && jobId !== null
        ? `/organizations/${organizationId}/calendars/${calendarId}/sync_jobs/${jobId}/for_syncing`
        : organizationId !== null && calendarId !== null
          ? `/organizations/${organizationId}/calendars/${calendarId}/sync_jobs/for_syncing`
          : organizationId !== null
            ? `/organizations/${organizationId}/calendars/sync_jobs/for_syncing`
            : `/organizations/calendars/sync_jobs/for_syncing`;

    const params = new URLSearchParams();
    if (sources !== null) {
      sources.forEach((e) => {
        params.append('calendarSource', e);
      });
    }
    if (types !== null) {
      types.forEach((e) => {
        params.append('calendarType', e);
      });
    }
    url += `?${params.toString()}`;

    return this.requestWithStatus<IPendingCalendarSyncJob>('PUT', url, null, {
      authHeader: `Basic ${authorization}`
    });
  }

  async markSyncJobAsSynced(
    authorization: string,
    organizationId: string,
    calendarId: string,
    jobId: string,
    syncToken: string | null,
    pageToken: string | null
  ) {
    return this.requestWithStatus<ICalendarSyncJob>(
      'PUT',
      `/organizations/${organizationId}/calendars/${calendarId}/sync_jobs/${jobId}/synced`,
      { sync_token: syncToken, page_token: pageToken },
      { authHeader: `Basic ${authorization}` }
    );
  }

  async markSyncJobAsFinished(
    authorization: string,
    organizationId: string,
    calendarId: string,
    jobId: string
  ) {
    return this.requestWithStatus<ICalendarSyncJob>(
      'PUT',
      `/organizations/${organizationId}/calendars/${calendarId}/sync_jobs/${jobId}/finished`,
      null,
      { authHeader: `Basic ${authorization}` }
    );
  }

  async markSyncJobAsFailed(
    authorization: string,
    organizationId: string,
    calendarId: string,
    jobId: string
  ) {
    return this.requestWithStatus<ICalendarSyncJob>(
      'PUT',
      `/organizations/${organizationId}/calendars/${calendarId}/sync_jobs/${jobId}/failed`,
      null,
      { authHeader: `Basic ${authorization}` }
    );
  }

  async createOrUpdateAuthorization(
    authorization: string,
    organizationId: string,
    request: CalendarAuthorizationCreateOrUpdateRequest
  ) {
    return this.requestWithStatus<ICalendarAuthorization>(
      'PUT',
      `/organizations/${organizationId}/calendars/authorizations`,
      request,
      { authHeader: `Basic ${authorization}` }
    );
  }

  async getAuthorizationForSyncing(
    authorization: string,
    organizationId: string,
    calendarSource: CalendarSource,
    authorizationId: string | null = null
  ) {
    const url =
      authorizationId !== null
        ? `/organizations/${organizationId}/calendars/authorizations/${authorizationId}/for_syncing?calendarSource=${encodeURIComponent(calendarSource)}`
        : `/organizations/${organizationId}/calendars/authorizations/for_syncing?calendarSource=${encodeURIComponent(calendarSource)}`;
    return this.requestWithStatus<ICalendarAuthorizationForSyncing>(
      'PUT',
      url,
      null,
      { authHeader: `Basic ${authorization}` }
    );
  }

  async markAuthorizationAsSynced(
    authorization: string,
    organizationId: string,
    authorizationId: string
  ) {
    return this.requestWithStatus<ICalendarAuthorization>(
      'PUT',
      `/organizations/${organizationId}/calendars/authorizations/${authorizationId}/synced`,
      null,
      { authHeader: `Basic ${authorization}` }
    );
  }

  async markAuthorizationAsFailed(
    authorization: string,
    organizationId: string,
    authorizationId: string
  ) {
    return this.requestWithStatus<ICalendarAuthorization>(
      'PUT',
      `/organizations/${organizationId}/calendars/authorizations/${authorizationId}/failed`,
      null,
      { authHeader: `Basic ${authorization}` }
    );
  }

  async createOrUpdateAuthorizationIntent(
    authorization: string,
    organizationId: string,
    request: CalendarAuthorizationIntentCreateOrUpdateRequest
  ) {
    return this.requestWithStatus<ICalendarAuthorizationIntent>(
      'PUT',
      `/organizations/${organizationId}/calendars/authorizations/intents`,
      request,
      { authHeader: `Basic ${authorization}` }
    );
  }

  async getAuthorizationIntentForSyncing(
    authorization: string,
    organizationId: string | null,
    calendarSource: CalendarSource
  ) {
    let url = `/calendars/authorizations/intents/for_syncing?calendarSource=${encodeURIComponent(calendarSource)}`;
    if (organizationId !== null) {
      url += `&organization=${encodeURIComponent(organizationId)}`;
    }
    return this.requestWithStatus<ICalendarAuthorizationIntentForSyncing>(
      'PUT',
      url,
      null,
      { authHeader: `Basic ${authorization}` }
    );
  }

  async markAuthorizationIntentAsChecked(
    authorization: string,
    organizationId: string,
    intentId: string
  ) {
    return this.requestWithStatus<ICalendarAuthorizationIntent>(
      'PUT',
      `/organizations/${organizationId}/calendars/authorizations/intents/${intentId}/checked`,
      null,
      { authHeader: `Basic ${authorization}` }
    );
  }

  async getListenerListening(
    authorization: string,
    organizationId: string,
    id: string
  ) {
    return this.requestWithStatus<CalendarListenerListeningResponse>(
      'GET',
      `/organizations/${organizationId}/calendars/${id}/listeners/listening`,
      null,
      {
        authHeader: `Basic ${authorization}`
      }
    );
  }

  async createOrUpdateListener(
    authorization: string,
    organizationId: string,
    id: string,
    body: CalendarListenerCreateOrUpdateRequest,
    listenerId: string | null = null
  ) {
    return this.requestWithStatus<ICalendarListener>(
      listenerId !== null ? 'PUT' : 'POST',
      listenerId !== null
        ? `/organizations/${organizationId}/calendars/${id}/listeners/${listenerId}`
        : `/organizations/${organizationId}/calendars/${id}/listeners`,
      body,
      { authHeader: `Basic ${authorization}` }
    );
  }

  async markListenerAsStarted(
    authorization: string,
    organizationId: string,
    listenerId: string,
    body: CalendarMarkListenerAsStartedRequest
  ) {
    return this.requestWithStatus<ICalendarListener>(
      'PUT',
      `/organizations/${organizationId}/calendars/listeners/${listenerId}/started`,
      body,
      { authHeader: `Basic ${authorization}` }
    );
  }

  async markListenerAsStopped(
    authorization: string,
    organizationId: string,
    listenerId: string
  ) {
    return this.requestWithStatus<ICalendarListener>(
      'PUT',
      `/organizations/${organizationId}/calendars/listeners/${listenerId}/stopped`,
      null,
      { authHeader: `Basic ${authorization}` }
    );
  }

  async listenerNotified(
    authorization: string,
    organizationId: string,
    listenerId: string,
    token: string
  ) {
    return this.requestWithStatus<ICalendarListener>(
      'PUT',
      `/organizations/${organizationId}/calendars/listeners/${listenerId}/notified`,
      { token },
      { authHeader: `Basic ${authorization}` }
    );
  }

  async getForSyncCheck(
    authorization: string,
    organizationId: string | null = null,
    sources: CalendarSource[] | null = null,
    types: CalendarType[] | null = null
  ) {
    let url =
      organizationId !== null
        ? `/organizations/${organizationId}/calendars/for_sync_check`
        : `/calendars/for_sync_check`;
    const params = new URLSearchParams();
    if (sources !== null) {
      sources.forEach((e) => {
        params.append('calendarSource', e);
      });
    }
    if (types !== null) {
      types.forEach((e) => {
        params.append('calendarType', e);
      });
    }
    url += `?${params.toString()}`;
    return this.requestWithStatus<ICalendarSyncCheck>('GET', url, null, {
      authHeader: `Basic ${authorization}`
    });
  }

  async markAsSyncChecked(
    authorization: string,
    organizationId: string,
    calendarId: string
  ) {
    return this.requestWithStatus<ICalendarSyncJob>(
      'PUT',
      `/organizations/${organizationId}/calendars/${calendarId}/sync_checked`,
      null,
      { authHeader: `Basic ${authorization}` }
    );
  }
}
